// Custom Hook para Turnstile
import { useEffect, useState, useRef, useCallback } from 'react';

export const useTurnstile = (options = {}) => {
  const {
    // sitekey = process.env.REACT_APP_TURNSTILE_SITEKEY,
    sitekey = "0x4AAAAAAB_Emzyl90Z-7fUr",
    language = 'es',
    theme = 'light'
  } = options;

  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [widgetId, setWidgetId] = useState(null);
  const [btnDisabled, setBtnDisabled] = useState(true);
  const isTurnstileLoaded = useRef(false);

  // Inicializar Turnstile
  const initializeTurnstile = useCallback((containerId = 'my-turnstile-widget') => {
    if (isTurnstileLoaded.current) return;

    const container = document.getElementById(containerId);
    if (!container) {
      console.error(`Container #${containerId} no encontrado`);
      return;
    }

    try {
      const id = window.turnstile.render(container, {
        sitekey,
        language,
        theme,
        callback: (token) => {
          setCaptchaToken(token);
          setBtnDisabled(false);
          setIsScriptLoaded(true);
        },
        "expired-callback": () => {
          setCaptchaToken(null);
          setBtnDisabled(true);
        },
        "error-callback": () => {
          setCaptchaToken(null);
          setBtnDisabled(true);
        }
      });

      setWidgetId(id);
      isTurnstileLoaded.current = true;
      setIsScriptLoaded(false);
    } catch (error) {
      console.error("Error inicializando Turnstile:", error);
    }
  }, [sitekey, language, theme]);

  // Cargar script de Turnstile
  useEffect(() => {
    if (isTurnstileLoaded.current) return;

    const scriptId = 'turnstile-script';

    // Si el script ya existe, usar ese
    const existingScript = document.getElementById(scriptId);
    if (existingScript) {
      if (window.turnstile) {
        initializeTurnstile();
      } else {
        existingScript.onload = () => initializeTurnstile();
      }
      return;
    }

    // Crear nuevo script
    const script = document.createElement("script");
    script.id = scriptId;
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&hl=es";
    script.async = true;
    script.defer = true;

    script.onload = () => {
      if (window.turnstile && !isTurnstileLoaded.current) {
        initializeTurnstile();
      }
    };

    script.onerror = () => {
      console.error("Error al cargar el script de Turnstile");
      setIsScriptLoaded(false);
    };

    document.body.appendChild(script);

    return () => {
      // No remover el script para reutilización entre componentes
    };
  }, [initializeTurnstile]);


  // Limpiar al desmontar
  const cleanupTurnstile = useCallback(() => {
    if (widgetId && window.turnstile) {
      try {
        window.turnstile.remove(widgetId);
      } catch (error) {
        console.error("Error limpiando Turnstile:", error);
      }
    }
    setWidgetId(null);
    setCaptchaToken(null);
    isTurnstileLoaded.current = false;
  }, [widgetId]);

  // Resetear CAPTCHA
  const resetCaptcha = useCallback(() => {
    // Limpiar primero
    cleanupTurnstile();

    // Pequeño delay para asegurar que se limpia antes de reiniciar
    setTimeout(() => {
      if (window.turnstile) {
        initializeTurnstile();
      } else {
        console.warn('Turnstile not available for reset');
      }
    }, 300);
  }, [cleanupTurnstile, initializeTurnstile]);

  // Recrear widget (útil para cuando cambia el container)
  const recreateWidget = useCallback((newContainerId = 'my-turnstile-widget') => {
    cleanupTurnstile();
    setTimeout(() => initializeTurnstile(newContainerId), 100);
  }, [cleanupTurnstile, initializeTurnstile]);

  return {
    isScriptLoaded,
    captchaToken,
    btnDisabled,
    widgetId,
    resetCaptcha,
    cleanupTurnstile,
    recreateWidget,
    initializeTurnstile
  };
};